/*
 * Copyright (c) 2026 Bolmate B.V.
 *
 * Renders the "Push-meldingen" status card on the notifications page — an
 * ADMIN diagnostics panel (the page gates it to admins / admin-as-user).
 * The old enable/disable switch is gone: users manage their subscription from
 * each notification's channels select ("stuur een notificatie via ...").
 * What remains here: a manual refresh. Test sends are now per-row — a test push
 * per device (DeviceList) and a test e-mail per address (EmailRecipientsList) —
 * and the guard hints (why push can't work here) moved to the device list
 * section (PushGuardHint).
 */

import './style/pushNotifications.scss';

import React, {useContext} from 'react';

import {useTranslation} from 'react-i18next';

import IconWithLabel from '../icon/iconWithLabel';

import {PushChannelsContext} from './pushChannelsContext';

interface Props {
    /** Optional — call when the user clicks the manual refresh icon, so the
     *  page can refresh the sibling device list along with the provider. */
    onRefreshRequested?: () => void;
}

export default function PushNotificationsCard(props: Props) {
    const {t} = useTranslation();
    const pushContext = useContext(PushChannelsContext);
    const [refreshing, setRefreshing] = React.useState(false);

    // Rendered inside PushChannelsProvider by the notifications page; the guard
    // keeps an accidental out-of-provider mount from crashing.
    if (!pushContext) return null;
    const {refresh} = pushContext;

    async function onRefresh() {
        if (refreshing) return;
        setRefreshing(true);
        try {
            await refresh();
            props.onRefreshRequested?.();
        } finally {
            setRefreshing(false);
        }
    }

    return (
        <div className='push-card'>
            {/* The section title + admin badge live in the surrounding
                BolmateCollapsibleSection header (notificationsPage); the guard
                hints moved to the device list section (PushGuardHint) — the card
                only carries the actions. */}
            <div className='push-card-header'>
                <div className='push-card-actions'>
                    <IconWithLabel
                        label={t('push.refresh', 'Status en apparaten vernieuwen')}
                        name='arrows-rotate'
                        onClick={onRefresh}
                        iconClassName={'action btn-refresh' + (refreshing ? ' push-card-refresh-spinning' : '')}
                    />
                </div>
            </div>
        </div>
    );
}
